import { ReadObject, SaveObject } from './storage';
import { GET, POST } from './api';

const DAILY_MESSAGE_LIMIT = 20;
const FREE_MESSAGES_KEY = 'free_messages_count';

/**
 * Estrutura do contador local:
 * {
 *   userId: string,
 *   characters: {
 *     [characterId]: {
 *       count: number,
 *       lastReset: ISO date string (YYYY-MM-DD)
 *     }
 *   }
 * }
 */

// Obtém a data atual no formato YYYY-MM-DD
const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

// Reseta contador se mudou o dia
const resetIfNewDay = (characterData) => {
    const today = getTodayDate();
    if (!characterData || characterData.lastReset !== today) {
        return {
            count: 0,
            lastReset: today
        };
    }
    return characterData;
};

// Obtém contador de mensagens do localStorage
export const getMessageCount = async (characterId) => {
    try {
        const user = await ReadObject('user');
        if (!user?.id) return { count: 0, limit: DAILY_MESSAGE_LIMIT };

        const stored = await ReadObject(FREE_MESSAGES_KEY) || {};
        
        // Se mudou de usuário, limpa contador
        if (stored.userId !== user.id) {
            await SaveObject(FREE_MESSAGES_KEY, {
                userId: user.id,
                characters: {}
            });
            return { count: 0, limit: DAILY_MESSAGE_LIMIT };
        }

        const characterData = resetIfNewDay(stored.characters?.[characterId]);
        return {
            count: characterData.count || 0,
            limit: DAILY_MESSAGE_LIMIT,
            remaining: Math.max(0, DAILY_MESSAGE_LIMIT - (characterData.count || 0))
        };
    } catch (error) {
        console.error('Erro ao obter contador de mensagens:', error);
        return { count: 0, limit: DAILY_MESSAGE_LIMIT };
    }
};

// Incrementa contador de mensagens
export const incrementMessageCount = async (characterId) => {
    try {
        const user = await ReadObject('user');
        if (!user?.id) return false;

        const stored = await ReadObject(FREE_MESSAGES_KEY) || {
            userId: user.id,
            characters: {}
        };

        const characterData = resetIfNewDay(stored.characters?.[characterId]);
        characterData.count = (characterData.count || 0) + 1;

        stored.characters = stored.characters || {};
        stored.characters[characterId] = characterData;
        stored.userId = user.id;

        await SaveObject(FREE_MESSAGES_KEY, stored);

        // Tentar sincronizar com backend (não bloqueante)
        try {
            await syncMessageCountToBackend(characterId, characterData.count);
        } catch (err) {
            console.warn('Falha ao sincronizar contador com backend:', err);
        }

        return true;
    } catch (error) {
        console.error('Erro ao incrementar contador:', error);
        return false;
    }
};

// Verifica se usuário pode enviar mensagem
export const canSendMessage = async (characterId) => {
    try {
        // Verifica se é usuário premium
        const isPremium = await checkIfUserIsPremium();
        if (isPremium) return { allowed: true, isPremium: true };

        // Usuário gratuito: verifica limite
        const { count, limit } = await getMessageCount(characterId);
        return {
            allowed: count < limit,
            isPremium: false,
            count,
            limit,
            remaining: Math.max(0, limit - count)
        };
    } catch (error) {
        console.error('Erro ao verificar permissão de envio:', error);
        // Em caso de erro, permite enviar (fail-safe)
        return { allowed: true, error: true };
    }
};

// Verifica se usuário tem assinatura ativa
const checkIfUserIsPremium = async () => {
    try {
        const user = await ReadObject('user');
        if (!user?.id) return false;

        // Verifica campo account_type
        if (user.account_type === 'premium') return true;

        // Fallback: verificar assinatura via endpoint
        const response = await GET('/check-plan', true);
        if (!response) return false;

        const hasRecurrency = response.recurrency;
        const isCanceled = response?.subscription?.canceled;
        const isGiftCard = response.subscription_type === 'giftcard' || response.type === 'giftcard';
        const exp = response.expire_at || response.expires_at;
        const notExpired = exp ? (new Date(exp) > new Date()) : false;

        return (hasRecurrency && !isCanceled) || (isGiftCard && notExpired && !isCanceled);
    } catch (error) {
        console.error('Erro ao verificar status premium:', error);
        return false;
    }
};

// Sincroniza contador com backend (opcional, para analytics)
const syncMessageCountToBackend = async (characterId, count) => {
    try {
        const user = await ReadObject('user');
        if (!user?.id) return;

        await POST('/daily-message-logs', {
            data: {
                user_id: user.id,
                character_id: characterId,
                message_count: count,
                date: getTodayDate()
            }
        }, true);
    } catch (error) {
        // Não propaga erro - sync é opcional
        console.warn('Sync com backend falhou:', error);
    }
};

// Reseta todos os contadores (útil para testes)
export const resetAllCounters = async () => {
    await SaveObject(FREE_MESSAGES_KEY, null);
};
